import { createFileRoute } from "@tanstack/react-router";
import { Page, PageHead } from "@/components/site/Page";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy policy | CLOSDD" },
      {
        name: "description",
        content:
          "Your data, in plain words. Read how CLOSDD collects, uses and looks after your information.",
      },
    ],
  }),
  component: Privacy,
});

const sections = [
  { id: "who-we-are", title: "Who we are" },
  { id: "what-we-collect", title: "What we collect" },
  { id: "how-we-use-it", title: "How we use it" },
  { id: "who-sees-it", title: "Who sees it" },
  { id: "how-long-we-keep-it", title: "How long we keep it" },
  { id: "your-choices", title: "Your choices" },
];

function Privacy() {
  return (
    <Page>
      <PageHead
        eyebrow="Privacy policy · 28 October 2026"
        title="Your data, in plain words."
        intro="We use your information to run CLOSDD, connect employers with salespeople and keep your account secure. Here is what that means."
      />
      <main className="mx-auto grid w-full max-w-[1216px] gap-12 px-5 py-12 lg:grid-cols-[240px_1fr] lg:px-8 lg:py-16">
        <nav aria-label="On this page" className="h-fit lg:sticky lg:top-6">
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-coral-ink">
            On this page
          </p>
          <ol className="mt-4 space-y-2">
            {sections.map((section) => (
              <li key={section.id}>
                <a className="text-[15px] text-ink/70 hover:text-navy" href={`#${section.id}`}>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="max-w-[780px] space-y-12">
          <section id="who-we-are">
            <h2 className="text-2xl font-extrabold text-navy">Who we are</h2>
            <p className="mt-4 leading-7 text-ink/70">
              CLOSDD is a sales hiring platform. We help salespeople create profiles and apply for
              roles, and help employers publish roles and review applications. The service is
              operated by Closdd Ltd, company number <strong>[company number]</strong>, registered
              office <strong>167 City Rd, London EC1V 1AW</strong>.
            </p>
            <p className="mt-3 leading-7 text-ink/70">
              For privacy questions, contact{" "}
              <a className="underline" href="mailto:info@closdd.com">
                info@closdd.com
              </a>
              .
            </p>
          </section>
          <section id="what-we-collect">
            <h2 className="text-2xl font-extrabold text-navy">What we collect</h2>
            <p className="mt-4 leading-7 text-ink/70">
              We collect details you give us, such as your name, contact details, work history,
              profile information, CV, pitch recordings, assessment answers and job applications.
              Employers may provide company, contact and role information. We also receive basic
              technical and usage information when you use the site.
            </p>
          </section>
          <section id="how-we-use-it">
            <h2 className="text-2xl font-extrabold text-navy">How we use it</h2>
            <p className="mt-4 leading-7 text-ink/70">
              We use information to provide and improve the service, create and manage accounts,
              show applications to employers, help candidates apply for roles, respond to requests,
              protect the platform and meet legal obligations.
            </p>
          </section>
          <section id="who-sees-it">
            <h2 className="text-2xl font-extrabold text-navy">Who sees it</h2>
            <p className="mt-4 leading-7 text-ink/70">
              Your profile and application information is shared with employers when you apply.
              Information may also be handled by service providers who help us operate the site, and
              by authorities where the law requires it. We do not sell personal information.
            </p>
          </section>
          <section id="how-long-we-keep-it">
            <h2 className="text-2xl font-extrabold text-navy">How long we keep it</h2>
            <p className="mt-4 leading-7 text-ink/70">
              We keep account and application information while it is needed to provide the service,
              and for a reasonable period afterwards for legal, safety and record-keeping purposes.
              You can ask us to delete your account and personal information, subject to information
              we must keep by law.
            </p>
          </section>
          <section id="your-choices">
            <h2 className="text-2xl font-extrabold text-navy">Your choices</h2>
            <p className="mt-4 leading-7 text-ink/70">
              You can review or update account details, choose what to include in your profile,
              withdraw an application where available, or contact us about access, correction or
              deletion. You can also complain to the UK Information Commissioner’s Office.
            </p>
            <h3 id="cookies" className="mt-8 text-lg font-bold text-navy">
              Cookies
            </h3>
            <p className="mt-3 leading-7 text-ink/70">
              CLOSDD uses cookies and similar browser storage where needed for the site to work and
              to remember settings. You can control cookies through your browser; disabling them may
              affect some features.
            </p>
          </section>
          <p className="border-t border-purple-strong/10 pt-5 text-sm text-ink/55">
            Last updated 28 October 2026.
          </p>
        </div>
      </main>
    </Page>
  );
}
